/**
 * captureConsent.js — per-user localStorage consent tracking for chat capture.
 *
 * Key format: chat_capture_consent_v1_{user_id}
 *   - v1 lets us version the consent prompt if the disclosure text changes later
 *   - per-user keying ensures logout/login-as-different-user re-shows the banner
 *
 * Stored value: the string 'true' or 'false'. `null` means "never asked".
 *
 * Defensive: every localStorage operation is wrapped in try/catch. Some
 * browsers / privacy modes throw on access (Safari private mode, disabled
 * cookies, storage quota). In those cases:
 *   - hasConsent  returns null every time (banner will keep appearing,
 *                 acceptable because the backend kill switch is the real gate)
 *   - setConsent  silently no-ops
 *   - clearConsent silently no-ops
 * We log one [CHAT-CAPTURE-CONSENT] warning on first failure — the app never
 * crashes from storage errors.
 */

const KEY_PREFIX = 'chat_capture_consent_v1_';
const LOG_PREFIX = '[CHAT-CAPTURE-CONSENT]';


function keyFor(userId) {
  return `${KEY_PREFIX}${userId}`;
}


/**
 * Returns: true | false | null
 *   true  — user clicked Allow
 *   false — user clicked Not now / Decline
 *   null  — never asked, OR userId missing, OR storage unavailable
 */
export function hasConsent(userId) {
  if (userId == null) return null;
  try {
    const raw = window.localStorage.getItem(keyFor(userId));
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return null;
  } catch (e) {
    console.warn(`${LOG_PREFIX} hasConsent read failed:`, e?.message || e);
    return null;
  }
}


/**
 * Write the user's choice. userId required; allowed coerced to boolean.
 * No-op on storage failure.
 */
export function setConsent(userId, allowed) {
  if (userId == null) return;
  try {
    window.localStorage.setItem(keyFor(userId), allowed ? 'true' : 'false');
  } catch (e) {
    console.warn(`${LOG_PREFIX} setConsent write failed:`, e?.message || e);
  }
}


/**
 * Remove the stored consent for a user. No-op on storage failure or
 * missing userId.
 */
export function clearConsent(userId) {
  if (userId == null) return;
  try {
    window.localStorage.removeItem(keyFor(userId));
  } catch (e) {
    console.warn(`${LOG_PREFIX} clearConsent remove failed:`, e?.message || e);
  }
}
